// frontend/src/hooks/useGroups.ts - COMPLETE FIXED VERSION
// Entity-specific hooks for groups with comprehensive backend integration

import { useCallback } from 'react';
import apiClient from '@/api';
import { useAuthStore } from '@/store/auth';
import { 
  useApi, 
  useMutation, 
  validateObjectId, 
  validateObjectIds, 
  normalizeSingleResponse, 
  normalizePaginatedResponse,
  type UseApiOptions,
  type UseMutationOptions
} from './useApi';

// Import only types from API - not functions
import type {
  // Response types
  StandardResponse,
  PaginatedResponse,
  GroupListResponse,
  
  // Group types
  Group,
  GroupCreateRequest,
  GroupUpdateRequest,
  GroupsQueryParams,
  AddMembersRequest,
  
  // User types
  User,
  
  // Query parameter types
  QueryParams
} from '@/api/types';

// ===== BACKEND RESPONSE INTERFACES =====

/**
 * Backend groups list response format (based on groups.js analysis)
 */
interface BackendGroupListResponse {
  groups: Group[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    currentPage?: number;
    itemsPerPage?: number;
  };
  analytics?: {
    totalGroups: number;
    visibilityGroups: number;
    approvalGroups: number;
    systemGroups: number;
    totalMembers: number;
  };
  organization?: {
    _id: string;
    name: string;
  };
  accessLevel?: string;
  filters?: any;
}

/**
 * Backend single group response format
 */
interface BackendGroupResponse {
  success?: boolean;
  data?: Group;
  group?: Group;
  message?: string;
  error?: string;
  analytics?: any;
  organization?: any;
}

/**
 * Backend group operation response format
 */
interface BackendGroupOperationResponse {
  success: boolean;
  message: string;
  group?: Group;
  data?: Group;
  addedCount?: number;
  totalMembers?: number;
  removed?: boolean;
}

// ===== TYPE GUARDS =====

function isBackendGroupListResponse(response: any): response is BackendGroupListResponse {
  return response && 
    typeof response === 'object' && 
    Array.isArray(response.groups) && 
    response.pagination &&
    typeof response.pagination.page === 'number';
}

function isBackendGroupResponse(response: any): response is BackendGroupResponse {
  return response && 
    typeof response === 'object' && 
    (response.data || response.group || response._id || response.name);
}

function isBackendGroupOperationResponse(response: any): response is BackendGroupOperationResponse {
  return response && 
    typeof response === 'object' && 
    typeof response.success === 'boolean' &&
    typeof response.message === 'string';
}

// ===== GROUP QUERY HOOKS =====

/**
 * Fetch paginated list of groups for an organization with security validation
 */
export const useGroups = (organizationId?: string, params?: GroupsQueryParams, options?: UseApiOptions) => {
  const { user: currentUser } = useAuthStore();
  
  return useApi(
    async (): Promise<PaginatedResponse<Group>> => {
      if (!organizationId || organizationId.trim() === '') {
        return {
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: 10,
            hasNextPage: false,
            hasPrevPage: false
          },
          total: 0,
          totalPages: 0,
          page: 1,
          limit: 10
        };
      }
      
      validateObjectId(organizationId, 'Organization ID');
      
      
      // Security validation - only implementors can query across organizations
      if (currentUser?.role !== 'implementor') {
        let userOrgId: string | undefined;
        
        if (currentUser?.organization) {
          if (typeof currentUser.organization === 'string') {
            userOrgId = currentUser.organization;
          } else if ((currentUser.organization as any)._id) {
            userOrgId = (currentUser.organization as any)._id;
          }
        }
        
        if (userOrgId && organizationId !== userOrgId) {
          console.warn('useGroups: Security - User attempting to access different organization groups');
          throw new Error('Access denied: You can only view groups from your own organization');
        }
      }
      
      // Check if groups API exists and use proper method
      if (!apiClient.groups || !('list' in apiClient.groups)) {
        throw new Error('Groups API not available in current API version');
      }
      
      const response = await (apiClient.groups as any).list(organizationId, params);
      
      // FIXED: Handle the backend response format correctly
      // The backend returns: { groups: Array, pagination: {...}, analytics: {...} }
      // But normalizePaginatedResponse expects: { data: Array, pagination: {...} }
      
      if (isBackendGroupListResponse(response)) {
        
        // Transform to the expected frontend format
        const transformedResponse: PaginatedResponse<Group> = {
          data: response.groups,  // Move groups array to data field
          pagination: {
            currentPage: response.pagination.currentPage || response.pagination.page,
            totalPages: response.pagination.totalPages,
            totalItems: response.pagination.total,
            itemsPerPage: response.pagination.itemsPerPage || response.pagination.limit,
            hasNextPage: response.pagination.hasNextPage,
            hasPrevPage: response.pagination.hasPrevPage
          },
          total: response.pagination.total,
          totalPages: response.pagination.totalPages,
          page: response.pagination.currentPage || response.pagination.page,
          limit: response.pagination.itemsPerPage || response.pagination.limit,
          // Include additional metadata - organization goes in metadata since it's not part of PaginatedResponse type
          analytics: response.analytics,
          metadata: {
            filters: response.filters,
            organization: response.organization,
            accessLevel: response.accessLevel
          }
        };
        
        
        return transformedResponse;
      }
      
      // If already in expected format, use normalization
      return normalizePaginatedResponse<Group>(response);
    },
    {
      immediate: !!(organizationId && organizationId.trim()),
      dependencies: [
        organizationId || 'undefined', 
        JSON.stringify(params || {}),
        currentUser?.role
      ],
      retryCount: 1,
      retryDelay: 2000,
      debounce: 300,
      rateLimitKey: `groups_${organizationId || 'undefined'}_${currentUser?.role}_${params?.type || 'all'}`,
      ...options
    }
  );
};

/**
 * Fetch single group by ID with access validation
 */
export const useGroup = (organizationId?: string, groupId?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<Group> => {
      if (!organizationId || !groupId) {
        throw new Error('Organization ID and Group ID are required');
      }
      
      validateObjectId(organizationId, 'Organization ID');
      validateObjectId(groupId, 'Group ID');
      
      
      if (!apiClient.groups || !('get' in apiClient.groups)) {
        throw new Error('Groups API not available in current API version');
      }
      
      const response = await (apiClient.groups as any).get(organizationId, groupId);
      
      // Handle different backend response formats
      if (isBackendGroupResponse(response)) {
        let group: Group | undefined;
        
        if (response.data) {
          // StandardResponse<Group> format: { data: Group }
          group = response.data;
        } else if (response.group) {
          // Alternative format: { group: Group }
          group = response.group;
        } else if ((response as any)._id || (response as any).name) {
          // Direct group object
          group = response as Group;
        }
        
        if (!group) {
          console.error('useGroup: Could not extract group from response:', response);
          throw new Error('Invalid group data received from server');
        }
        
        // Validate group object
        if (!group._id && !group.id) {
          throw new Error('Group object missing required ID field');
        }
        
        return group;
      }
      
      // Fallback to normalization
      return normalizeSingleResponse<Group>(response);
    },
    {
      immediate: !!(organizationId && groupId),
      retryCount: 1,
      retryDelay: 2000,
      rateLimitKey: `group_${organizationId}_${groupId}`,
      ...options
    }
  );
};

/**
 * Fetch cross-organization groups (implementor only)
 */
export const useCrossOrgGroups = (params?: GroupsQueryParams, options?: UseApiOptions) => {
  const { user: currentUser } = useAuthStore();
  
  return useApi(
    async (): Promise<PaginatedResponse<Group>> => {
      // Role validation
      if (currentUser?.role !== 'implementor') {
        throw new Error('Access denied: Only implementors can view cross-organization groups');
      }
      
      
      // Check if cross-org groups API exists
      if (!apiClient.groups || !('listCrossOrg' in apiClient.groups)) {
        throw new Error('Cross-organization groups API not available in current API version');
      }
      
      const response = await (apiClient.groups as any).listCrossOrg(params);
      
      // Handle response format (likely similar to regular groups list)
      if (isBackendGroupListResponse(response)) {
        return {
          data: response.groups,
          pagination: {
            currentPage: response.pagination.currentPage || response.pagination.page,
            totalPages: response.pagination.totalPages,
            totalItems: response.pagination.total,
            itemsPerPage: response.pagination.itemsPerPage || response.pagination.limit,
            hasNextPage: response.pagination.hasNextPage,
            hasPrevPage: response.pagination.hasPrevPage
          },
          total: response.pagination.total,
          totalPages: response.pagination.totalPages,
          page: response.pagination.currentPage || response.pagination.page,
          limit: response.pagination.itemsPerPage || response.pagination.limit
        };
      }
      
      return normalizePaginatedResponse<Group>(response);
    },
    {
      immediate: currentUser?.role === 'implementor',
      dependencies: [JSON.stringify(params || {}), currentUser?.role],
      rateLimitKey: 'cross_org_groups',
      ...options
    }
  );
};

/**
 * Fetch group members
 */
export const useGroupMembers = (organizationId?: string, groupId?: string, params?: QueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<User>> => {
      if (!organizationId || !groupId) {
        return {
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: 10,
            hasNextPage: false,
            hasPrevPage: false
          },
          total: 0,
          totalPages: 0,
          page: 1,
          limit: 10
        };
      }
      
      // FIXED: Check for empty string organizationId before validation
      if (organizationId.trim() === '' || groupId.trim() === '') {
        return {
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: 10,
            hasNextPage: false,
            hasPrevPage: false
          },
          total: 0,
          totalPages: 0,
          page: 1,
          limit: 10
        };
      }
      
      validateObjectId(organizationId, 'Organization ID');
      validateObjectId(groupId, 'Group ID');
      
      
      // Check if getMembers method exists
      if (!apiClient.groups || !('getMembers' in apiClient.groups)) {
        throw new Error('Group members API not available in current API version');
      }
      
      const response = await (apiClient.groups as any).getMembers(organizationId, groupId, params);
      
      return normalizePaginatedResponse<User>(response);
    },
    {
      immediate: !!(organizationId && organizationId.trim() && groupId && groupId.trim()),
      dependencies: [organizationId, groupId, JSON.stringify(params || {})],
      rateLimitKey: `group_members_${organizationId}_${groupId}`,
      ...options
    }
  );
};

/**
 * Fetch group statistics
 */
export const useGroupStats = (organizationId?: string, groupId?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<any> => {
      if (!organizationId || !groupId) {
        return null;
      }
      
      validateObjectId(organizationId, 'Organization ID');
      validateObjectId(groupId, 'Group ID');
      
      
      // Check if getStats method exists
      if (!apiClient.groups || !('getStats' in apiClient.groups)) {
        throw new Error('Group statistics endpoint not available');
      }
      
      const response = await (apiClient.groups as any).getStats(organizationId!, groupId!);
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: false, // Manual trigger for stats
      rateLimitKey: `group_stats_${organizationId}_${groupId}`,
      ...options
    }
  );
};

// ===== GROUP MUTATION HOOKS =====

/**
 * Create new group with validation
 */
export const useCreateGroup = (options?: UseMutationOptions<Group, { organizationId: string; data: GroupCreateRequest }>) => {
  const { user: currentUser } = useAuthStore();
  
  return useMutation(
    async ({ organizationId, data }: { organizationId: string; data: GroupCreateRequest }): Promise<Group> => {
      validateObjectId(organizationId, 'Organization ID');
      
      // Validation
      if (!data.name?.trim()) {
        throw new Error('Group name is required');
      }
      
      if (data.name.length < 2) {
        throw new Error('Group name must be at least 2 characters long');
      }
      
      if (data.name.length > 100) {
        throw new Error('Group name must be less than 100 characters');
      }
      
      if (!data.type) {
        throw new Error('Group type is required');
      }
      
      if (!['visibility', 'approval'].includes(data.type)) {
        throw new Error('Group type must be either visibility or approval');
      }
      
      // Security validation - only implementors can create across organizations
      if (currentUser?.role !== 'implementor') {
        let userOrgId: string | undefined;
        
        if (currentUser?.organization) {
          if (typeof currentUser.organization === 'string') {
            userOrgId = currentUser.organization;
          } else if ((currentUser.organization as any)._id) {
            userOrgId = (currentUser.organization as any)._id;
          }
        }
        
        if (userOrgId && organizationId !== userOrgId) {
          throw new Error('Access denied: You can only create groups in your own organization');
        }
      }
      
      
      // Check if create method exists
      if (!apiClient.groups || !('create' in apiClient.groups)) {
        throw new Error('Group creation not available in current API version');
      }
      
      const response = await (apiClient.groups as any).create(organizationId, data);
      
      return normalizeSingleResponse<Group>(response);
    },
    options
  );
};

/**
 * Update existing group
 */
export const useUpdateGroup = (options?: UseMutationOptions<Group, { organizationId: string; groupId: string; data: GroupUpdateRequest }>) => {
  const { user: currentUser } = useAuthStore();
  
  return useMutation(
    async ({ organizationId, groupId, data }: { organizationId: string; groupId: string; data: GroupUpdateRequest }): Promise<Group> => {
      validateObjectId(organizationId, 'Organization ID');
      validateObjectId(groupId, 'Group ID');
      
      // Validation
      if (data.name !== undefined) {
        if (!data.name?.trim()) {
          throw new Error('Group name cannot be empty');
        }
        
        if (data.name.length < 2) {
          throw new Error('Group name must be at least 2 characters long');
        }
        
        if (data.name.length > 100) {
          throw new Error('Group name must be less than 100 characters');
        }
      }
      
      // Note: Group type cannot be changed after creation for security reasons
      // This is intentionally not included in GroupUpdateRequest
      
      // Security validation
      if (currentUser?.role !== 'implementor') {
        let userOrgId: string | undefined;
        
        if (currentUser?.organization) {
          if (typeof currentUser.organization === 'string') {
            userOrgId = currentUser.organization;
          } else if ((currentUser.organization as any)._id) {
            userOrgId = (currentUser.organization as any)._id;
          }
        }
        
        if (userOrgId && organizationId !== userOrgId) {
          throw new Error('Access denied: You can only update groups in your own organization');
        }
      }
      
      
      if (!apiClient.groups || !('update' in apiClient.groups)) {
        throw new Error('Group update not available in current API version');
      }
      
      const response = await (apiClient.groups as any).update(organizationId, groupId, data);
      
      return normalizeSingleResponse<Group>(response);
    },
    options
  );
};

/**
 * Delete group
 */
export const useDeleteGroup = (options?: UseMutationOptions<void, { organizationId: string; groupId: string }>) => {
  const { user: currentUser } = useAuthStore();
  
  return useMutation(
    async ({ organizationId, groupId }: { organizationId: string; groupId: string }): Promise<void> => {
      validateObjectId(organizationId, 'Organization ID');
      validateObjectId(groupId, 'Group ID');
      
      // Security validation
      if (currentUser?.role !== 'implementor' && currentUser?.role !== 'admin') {
        throw new Error('Access denied: Only administrators can delete groups');
      }
      
      if (currentUser?.role !== 'implementor') {
        let userOrgId: string | undefined;
        
        if (currentUser?.organization) {
          if (typeof currentUser.organization === 'string') {
            userOrgId = currentUser.organization;
          } else if ((currentUser.organization as any)._id) {
            userOrgId = (currentUser.organization as any)._id;
          }
        }
        
        if (userOrgId && organizationId !== userOrgId) {
          throw new Error('Access denied: You can only delete groups in your own organization');
        }
      }
      
      
      if (!apiClient.groups || !('delete' in apiClient.groups)) {
        throw new Error('Group deletion not available in current API version');
      }
      
      const response = await (apiClient.groups as any).delete(organizationId, groupId);
      
      return normalizeSingleResponse<void>(response);
    },
    options
  );
};

/**
 * Add members to group
 */
export const useAddGroupMembers = (options?: UseMutationOptions<any, { organizationId: string; groupId: string; memberData: AddMembersRequest }>) => {
  return useMutation(
    async ({ organizationId, groupId, memberData }: { organizationId: string; groupId: string; memberData: AddMembersRequest }): Promise<any> => {
      validateObjectId(organizationId, 'Organization ID');
      validateObjectId(groupId, 'Group ID');
      
      if (!memberData.userIds || memberData.userIds.length === 0) {
        throw new Error('At least one user ID is required');
      }
      
      // Validate all user IDs
      validateObjectIds(memberData.userIds, 'User IDs');
      
      
      if (!apiClient.groups || !('addMembers' in apiClient.groups)) {
        throw new Error('Add group members not available in current API version');
      }
      
      const response = await (apiClient.groups as any).addMembers(organizationId, groupId, memberData);
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Remove member from group
 */
export const useRemoveGroupMember = (options?: UseMutationOptions<void, { organizationId: string; groupId: string; userId: string }>) => {
  return useMutation(
    async ({ organizationId, groupId, userId }: { organizationId: string; groupId: string; userId: string }): Promise<void> => {
      validateObjectId(organizationId, 'Organization ID');
      validateObjectId(groupId, 'Group ID');
      validateObjectId(userId, 'User ID');


      if (!apiClient.groups || !('removeMember' in apiClient.groups)) {
        throw new Error('Remove group member not available in current API version');
      }

      const response = await (apiClient.groups as any).removeMember(organizationId, groupId, userId);

      return normalizeSingleResponse<void>(response);
    },
    options
  );
};

/**
 * Leave group (for contributors to leave groups themselves)
 */
export const useLeaveGroup = (options?: UseMutationOptions<any, { organizationId: string; groupId: string }>) => {
  return useMutation(
    async ({ organizationId, groupId }: { organizationId: string; groupId: string }): Promise<any> => {
      validateObjectId(organizationId, 'Organization ID');
      validateObjectId(groupId, 'Group ID');


      if (!apiClient.groups || !('leaveGroup' in apiClient.groups)) {
        throw new Error('Leave group not available in current API version');
      }

      const response = await (apiClient.groups as any).leaveGroup(organizationId, groupId);

      return normalizeSingleResponse<any>(response);
    },
    options
  );
};

/**
 * Batch group operations (implementor only)
 */
export const useBatchGroupOperations = (options?: UseMutationOptions<any, { operation: 'activate' | 'deactivate' | 'delete'; groupIds: { organizationId: string; groupId: string }[] }>) => {
  const { user: currentUser } = useAuthStore();
  
  return useMutation(
    async ({ operation, groupIds }: { operation: 'activate' | 'deactivate' | 'delete'; groupIds: { organizationId: string; groupId: string }[] }): Promise<any> => {
      // Role validation
      if (currentUser?.role !== 'implementor') {
        throw new Error('Access denied: Only implementors can perform batch group operations');
      }
      
      if (!groupIds || groupIds.length === 0) {
        throw new Error('At least one group ID is required');
      }
      
      if (!['activate', 'deactivate', 'delete'].includes(operation)) {
        throw new Error('Invalid operation. Must be activate, deactivate, or delete');
      }
      
      
      // Note: This would require a batch endpoint in the API
      // For now, we'll perform individual operations
      const results = [];
      for (const { organizationId, groupId } of groupIds) {
        try {
          let result;
          if (operation === 'delete') {
            result = await (apiClient.groups as any).delete(organizationId, groupId);
          } else {
            // For activate/deactivate, we'd need specific endpoints
            result = await (apiClient.groups as any).update(organizationId, groupId, {
              isActive: operation === 'activate'
            });
          }
          results.push({ organizationId, groupId, success: true, result });
        } catch (error: any) {
          results.push({ 
            organizationId, 
            groupId, 
            success: false, 
            error: error.message 
          });
        }
      }
      
      return results;
    },
    options
  );
};

// ===== SEARCH AND ANALYTICS HOOKS =====

/**
 * Search groups within organization
 */
export const useSearchGroups = (organizationId?: string, query?: string, filters?: any, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<Group>> => {
      if (!organizationId || !query?.trim()) {
        return {
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: 10,
            hasNextPage: false,
            hasPrevPage: false
          },
          total: 0,
          totalPages: 0,
          page: 1,
          limit: 10
        };
      }
      
      validateObjectId(organizationId, 'Organization ID');
      
      
      // Check if search endpoint exists, otherwise fall back to list with search params
      if (apiClient.groups && 'search' in apiClient.groups) {
        const response = await (apiClient.groups as any).search(organizationId, query, filters);
        return normalizePaginatedResponse<Group>(response);
      } else if (apiClient.groups && 'list' in apiClient.groups) {
        // Fallback to list with search parameter
        const searchParams: GroupsQueryParams = {
          search: query,
          ...filters
        };
        
        const response = await (apiClient.groups as any).list(organizationId, searchParams);
        
        // Handle the same response transformation as useGroups
        if (isBackendGroupListResponse(response)) {
          return {
            data: response.groups,
            pagination: {
              currentPage: response.pagination.currentPage || response.pagination.page,
              totalPages: response.pagination.totalPages,
              totalItems: response.pagination.total,
              itemsPerPage: response.pagination.itemsPerPage || response.pagination.limit,
              hasNextPage: response.pagination.hasNextPage,
              hasPrevPage: response.pagination.hasPrevPage
            },
            total: response.pagination.total,
            totalPages: response.pagination.totalPages,
            page: response.pagination.currentPage || response.pagination.page,
            limit: response.pagination.itemsPerPage || response.pagination.limit
          };
        }
        
        return normalizePaginatedResponse<Group>(response);
      } else {
        throw new Error('Groups search not available in current API version');
      }
    },
    {
      immediate: !!(organizationId && query?.trim()),
      dependencies: [organizationId, query, JSON.stringify(filters || {})],
      debounce: 300,
      retryCount: 2,
      rateLimitKey: 'search_groups',
      ...options
    }
  );
};

/**
 * Group analytics and reporting
 */
export const useGroupAnalytics = (params?: any, options?: UseApiOptions) => {
  return useApi(
    async () => {
      
      // Check if analytics endpoint exists
      if (!apiClient.groups || !('getAnalytics' in apiClient.groups)) {
        throw new Error('Group analytics endpoint not available');
      }
      
      const response = await (apiClient.groups as any).getAnalytics(params);
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: false, // Manual trigger for analytics
      dependencies: params ? [JSON.stringify(params)] : [],
      retryCount: 1,
      rateLimitKey: 'group_analytics',
      ...options
    }
  );
};

// ===== EXPORTS =====

// Export all group-related hooks
export {
  // Query hooks - already exported above
  // useGroups, useGroup, useCrossOrgGroups, useGroupMembers, useGroupStats
  
  // Mutation hooks - already exported above  
  // useCreateGroup, useUpdateGroup, useDeleteGroup
  // useAddGroupMembers, useRemoveGroupMember, useBatchGroupOperations
  
  // Search and analytics - already exported above
  // useSearchGroups, useGroupAnalytics
};

// Default export for convenience
const groupHooks = {
  // Query hooks
  useGroups,
  useGroup,
  useCrossOrgGroups,
  useGroupMembers,
  useGroupStats,
  
  // Mutation hooks
  useCreateGroup,
  useUpdateGroup,
  useDeleteGroup,
  useAddGroupMembers,
  useRemoveGroupMember,
  useBatchGroupOperations,
  
  // Search and analytics
  useSearchGroups,
  useGroupAnalytics
};

export default groupHooks;